import { pluralise } from '@javascript/lib/utils';

import type { ModelVariantFilterProps } from '../modelVariantFilter';
import type { MultiSelectFilterProps } from '../multiSelectFilter';
import type { FilterProps } from '@javascript/types/InstantSearch';
import type { SingleSelectNumericFilterProps } from '../singleSelectNumericFilter';
import type { ToggleFilterProps } from '../units/instantSearchToggle';
import type { EngineSizeFilterProps } from '../engineSizeFilter';
import type { FilterBaseProps } from '../../../types/InstantSearch';

export type FilterType =
  | 'colour'
  | 'engine'
  | 'features'
  | 'make'
  | 'modelVariant'
  | 'multi'
  | 'numeric'
  | 'payment'
  | 'single'
  | 'toggle'
  | 'location';

type ModelVariantFilter = 'model' | 'variant';

type MultiSelectFilter =
  | 'bodyType'
  | 'doors'
  | 'driveTrain'
  | 'fuelType'
  | 'seats'
  | 'insuranceGroup';

type SingleSelectNumericFilter =
  | 'age'
  | 'fuelConsumption'
  | 'mileage'
  | 'roadTax';

type ToggleFilter = 'reserved' | 'promotion';

type SingleSelectFilter = 'transmissionType' | 'conditions';

type MultiSelectSchema = MultiSelectFilterProps & { filterType: 'multi' };

type ModelVariantSchema = ModelVariantFilterProps & {
  filterType: 'modelVariant';
};

type SingleSelectNumericFilterSchema = SingleSelectNumericFilterProps & {
  filterType: 'numeric';
};

type ColourFilterSchema = FilterBaseProps & { filterType: 'colour' };
type EngineSizeFilterSchema = EngineSizeFilterProps & {
  filterType: 'engine';
};
type FeaturesFilterSchema = FilterBaseProps & { filterType: 'features' };
type LocationFilterSchema = FilterBaseProps & { filterType: 'location' };
type MakeFilterSchema = FilterBaseProps & { filterType: 'make' };
type PaymentFilterSchema = FilterBaseProps & { filterType: 'payment' };
type SingleSelectSchema = FilterProps & { filterType: 'single' };
type ToggleFilterSchema = ToggleFilterProps & { filterType: 'toggle' };

export type FilterSchema<T extends FilterType> = T extends 'colour'
  ? ColourFilterSchema
  : T extends 'engine'
  ? EngineSizeFilterSchema
  : T extends 'features'
  ? FeaturesFilterSchema
  : T extends 'location'
  ? LocationFilterSchema
  : T extends 'make'
  ? MakeFilterSchema
  : T extends 'modelVariant'
  ? ModelVariantSchema
  : T extends 'multi'
  ? MultiSelectSchema
  : T extends 'numeric'
  ? SingleSelectNumericFilterSchema
  : T extends 'payment'
  ? PaymentFilterSchema
  : T extends 'single'
  ? SingleSelectSchema
  : T extends 'toggle'
  ? ToggleFilterSchema
  : never;

export type FilterTypeProps<T extends FilterType> = Omit<
  FilterSchema<T>,
  'filterType'
>;

type FiltersSchema = {
  colour: FilterSchema<'colour'>;
  engine: FilterSchema<'engine'>;
  features: FilterSchema<'features'>;
  location: FilterSchema<'location'>;
  make: FilterSchema<'make'>;
  payment: FilterSchema<'payment'>;
} & { [K in ModelVariantFilter]: FilterSchema<'modelVariant'> } & {
  [K in MultiSelectFilter]: FilterSchema<'multi'>;
} & {
  [K in SingleSelectFilter]: FilterSchema<'single'>;
} & {
  [K in SingleSelectNumericFilter]: FilterSchema<'numeric'>;
} & {
  [K in ToggleFilter]: FilterSchema<'toggle'>;
};

export const DEFAULT_FILTERS_SCHEMA: FiltersSchema = {
  make: {
    filterType: 'make',
  },
  model: {
    filterType: 'modelVariant',
    label: 'Models',
    attribute: 'makeModel.model',
    relatedAttributes: ['makeModel.variant'],
    parentAttribute: 'makeModel.make',
  },
  variant: {
    filterType: 'modelVariant',
    label: 'Model variants',
    attribute: 'makeModel.variant',
    relatedAttributes: '',
    parentAttribute: 'makeModel.model',
  },
  location: {
    filterType: 'location',
  },
  payment: {
    filterType: 'payment',
  },
  promotion: {
    attribute: 'promotion',
    filterType: 'toggle',
    id: 'promotion',
    label: 'Show Black Friday offers',
  },
  reserved: {
    attribute: 'isReserved',
    filterType: 'toggle',
    id: 'reserved',
    label: 'Hide reserved cars',
    toggledValue: false,
  },
  age: {
    filterType: 'numeric',
    label: 'Age',
    attribute: 'age',
    itemList: [
      { label: 'Any' },
      { label: 'Up to 1 year', end: 1 },
      { label: 'Up to 2 years', end: 2 },
      { label: 'Up to 3 years', end: 3 },
      { label: 'Up to 4 years', end: 4 },
      { label: 'Up to 5 years', end: 5 },
      { label: 'Up to 6 years', end: 6 },
      { label: 'Up to 7 years', end: 7 },
    ],
  },
  transmissionType: {
    filterType: 'single',
    label: 'Gearbox',
    attribute: 'transmissionType',
    sortBy: ['count:desc', 'name:asc'],
  },
  fuelType: {
    filterType: 'multi',
    label: 'Fuel type',
    attribute: 'fuelType',
    sortBy: ['count:desc', 'name:asc'],
  },
  mileage: {
    filterType: 'numeric',
    label: 'Mileage',
    attribute: 'mileage',
    itemList: [
      { label: 'Any' },
      { label: 'Up to 500 miles', end: 500 },
      { label: 'Up to 1000 miles', end: 1000 },
      { label: 'Up to 5000 miles', end: 5000 },
      { label: 'Up to 10,000 miles', end: 10000 },
      { label: 'Up to 20,000 miles', end: 20000 },
      { label: 'Up to 30,000 miles', end: 30000 },
      { label: 'Up to 40,000 miles', end: 40000 },
      { label: 'Up to 50,000 miles', end: 50000 },
      { label: 'Up to 60,000 miles', end: 60000 },
      { label: 'Up to 70,000 miles', end: 70000 },
      { label: 'Up to 80,000 miles', end: 80000 },
    ],
  },
  conditions: {
    filterType: 'single',
    label: 'Vehicle type',
    attribute: 'conditions',
    sortBy: ['count:desc', 'name:asc'],
  },
  bodyType: {
    filterType: 'multi',
    label: 'Body type',
    attribute: 'bodyType',
    sortBy: ['name:asc'],
  },
  engine: {
    filterType: 'engine',
    engineSizes: {
      maximum: [
        { label: 'Any', value: undefined },
        { label: '1.0 litre', value: 1044 },
        { label: '1.2 litre', value: 1244 },
        { label: '1.4 litre', value: 1444 },
        { label: '1.6 litre', value: 1644 },
        { label: '1.8 litre', value: 1844 },
        { label: '2.0 litre', value: 2044 },
        { label: '2.5 litre', value: 2544 },
        { label: '3.0 litre', value: 3044 },
        { label: '3.5 litre', value: 3544 },
        { label: '4.0 litre', value: 4044 },
        { label: '4.5 litre', value: 4544 },
        { label: '5.0 litre', value: 5044 },
        { label: '5.5 litre', value: 5544 },
        { label: '6.0 litre', value: 6044 },
        { label: '6.5 litre', value: 6544 },
      ],
      minimum: [
        { label: 'Any', value: undefined },
        { label: '1.0 litre', value: 945 },
        { label: '1.2 litre', value: 1145 },
        { label: '1.4 litre', value: 1345 },
        { label: '1.6 litre', value: 1545 },
        { label: '1.8 litre', value: 1745 },
        { label: '2.0 litre', value: 1945 },
        { label: '2.5 litre', value: 2445 },
        { label: '3.0 litre', value: 2945 },
        { label: '3.5 litre', value: 3445 },
        { label: '4.0 litre', value: 3945 },
        { label: '4.5 litre', value: 4445 },
        { label: '5.0 litre', value: 4945 },
        { label: '5.5 litre', value: 5445 },
        { label: '6.0 litre', value: 5945 },
        { label: '6.5 litre', value: 6445 },
      ],
    },
  },
  colour: { filterType: 'colour' },
  seats: {
    filterType: 'multi',
    label: 'Seats',
    attribute: 'seats',
    labelFormatter: (label: string) => `${label} ${pluralise(label, 'seat')}`,
  },
  doors: {
    filterType: 'multi',
    label: 'Doors',
    attribute: 'doors',
    labelFormatter: (label: string) => `${label} ${pluralise(label, 'door')}`,
  },
  driveTrain: {
    filterType: 'multi',
    label: 'Drive type',
    attribute: 'driveTrain',
    sortBy: ['name:asc'],
  },
  fuelConsumption: {
    filterType: 'numeric',
    label: 'Fuel consumption',
    labelSuffix: '(MPG)',
    attribute: 'fuelConsumption',
    itemList: [
      { label: 'Any' },
      { label: '40 MPG +', start: 40 },
      { label: '50 MPG +', start: 50 },
      { label: '60 MPG + ', start: 60 },
      { label: '70 MPG + ', start: 70 },
    ],
    explanation:
      'A car with a higher MPG figure can travel a longer distance on a smaller amount of fuel, meaning it is more efficient and can help keep costs low.',
  },
  roadTax: {
    filterType: 'numeric',
    label: 'Vehicle tax',
    labelSuffix: '(per year)',
    attribute: 'roadtax',
    itemList: [
      { label: 'Any' },
      //for a specific value, use start AND end
      { label: '£0', start: 0, end: 0 },
      { label: 'Up to £50', end: 50 },
      { label: 'Up to £200', end: 200 },
      { label: 'Up to £300', end: 300 },
      { label: 'Up to £400', end: 400 },
      { label: 'Up to £600', end: 600 },
    ],
    explanation:
      'Vehicle tax depends on when a car was first registered, CO2 emissions, fuel type, and engine size.',
  },
  features: {
    filterType: 'features',
  },
  insuranceGroup: {
    filterType: 'multi',
    label: 'Insurance group',
    labelFormatter: (label: string) => label.split('-').join(' - '),
    explanation: [
      'Insurance companies categorise cars based on their size, value, and the cost of repairs.',
      'Vehicles in lower groups generally attract cheaper insurance premiums.',
    ],
    attribute: 'insuranceGroupRange',
    sortBy: (a: { name: string }, b: { name: string }) => {
      const parseRange = (range: { name: string }) => {
        const [start] = range.name.split('-');
        return parseInt(start);
      };
      return parseRange(a) - parseRange(b);
    },
  },
};

//plucking off the filters that are not used in the MOTAB search
const {
  conditions,
  mileage,
  colour,
  location,
  roadTax,
  reserved,
  promotion,
  payment,
  age,
  insuranceGroup,
  ...remainingFilters
} = DEFAULT_FILTERS_SCHEMA;

export const MOTAB_FILTERS_SCHEMA = remainingFilters;
